import { Download, FileText, Users } from 'lucide-react';

type FileData = {
  selectedFiles: Array<{
    key?: string;
    name?: string;
    path?: string;
    type?: string;
    field?: string;
    description?: string;
  }>;
};

type DocumentSection = {
  page: string;
  section?: string;
  paragraph: string;
};

interface OrganizationStructureProps {
  data: {
    f1: FileData;
    f2: FileData;
    f3: FileData & DocumentSection;
    section1: {
      q1: string;
      byLaws: DocumentSection;
      charter: DocumentSection;
      articlesOfIncorporation: DocumentSection;
    };
    section2: {
      q2: string;
      byLaws: DocumentSection;
      charter: DocumentSection;
      isChartered: string;
      articlesOfIncorporation: DocumentSection;
    };
    section3: {
      q3: string;
      byLaws: DocumentSection;
      charter: DocumentSection;
      isSponsored: string;
      articlesOfIncorporation: DocumentSection;
    };
    boardMembers: any[];
    providedEvidence: string;
  };
  onDownload?: (key: string) => void;
}

function isFileData(value: any): value is FileData {
  return value && 'selectedFiles' in value;
}

export default function OrganizationStructure({ data, onDownload }: OrganizationStructureProps) {
  if (!data) return null;

  // Filter and cast the entries to ensure they are FileData type
  const documentFields = Object.entries(data).filter(([key, value]) => key.startsWith('f') && isFileData(value));

  const renderDocumentSection = (title: string, section: DocumentSection) => (
    <div className='bg-gray-50 p-4 rounded-lg'>
      <h4 className='font-medium text-gray-900 mb-2'>{title}</h4>
      <div className='grid grid-cols-3 gap-4 text-sm'>
        <div>
          <span className='text-gray-500'>Page:</span>
          <span className='ml-2'>{section.page || 'N/A'}</span>
        </div>
        {section.section && (
          <div>
            <span className='text-gray-500'>Section:</span>
            <span className='ml-2'>{section.section || 'N/A'}</span>
          </div>
        )}
        <div>
          <span className='text-gray-500'>Paragraph:</span>
          <span className='ml-2'>{section.paragraph || 'N/A'}</span>
        </div>
      </div>
    </div>
  );

  return (
    <section aria-labelledby='organization-structure-title' className='mt-8'>
      <div className='bg-white sm:rounded-lg'>
        <div className='py-5'>
          <div className='px-4 py-2 sm:px-6 border-l-4 border-gray-900'>
            <div className='flex items-center'>
              <Users className='w-6 h-6 text-gray-900 mr-2' />
              <h2 id='organization-structure-title' className='m-0 text-lg leading-6 font-medium text-gray-900'>
                Step 4: Organization Structure
              </h2>
            </div>
          </div>
        </div>

        {/* Documents Section */}
        <div className='border-t border-gray-200 px-4 py-5 sm:px-6'>
          <h3 className='text-lg font-medium text-gray-900 mb-4'>Documents</h3>
          <div className='space-y-3'>
            {documentFields.map(([key, value]) => {
              const fileData = value as FileData;
              const fileKey = fileData.selectedFiles?.[0]?.key;
              return (
                fileKey && (
                  <div key={key} className='flex items-center justify-between p-3 bg-gray-50 rounded-lg'>
                    <div className='flex items-center'>
                      <FileText className='w-5 h-5 text-gray-900 mr-2' />
                      <span className='text-sm text-gray-900'>Document {key.toUpperCase()}</span>
                    </div>
                    {onDownload && (
                      <button
                        onClick={() => onDownload(fileKey)}
                        className='flex items-center px-3 py-1 text-sm text-gray-700 hover:text-gray-900'
                      >
                        <Download className='w-4 h-4 mr-1' />
                        Download
                      </button>
                    )}
                  </div>
                )
              );
            })}
          </div>
        </div>

        {/* Section Details */}
        <div className='border-t border-gray-200 px-4 py-5 sm:px-6'>
          <div className='space-y-8'>
            {/* Section 1 */}
            <div>
              <div className='space-y-4'>
                <div className='bg-gray-50 p-4 rounded-lg'>
                  <span className='ml-2'>{data.section1.q1 || 'N/A'}</span>
                </div>
                {renderDocumentSection('Articles of Incorporation', data.section1.articlesOfIncorporation)}
                {renderDocumentSection('By Laws', data.section1.byLaws)}
                {renderDocumentSection('Charter', data.section1.charter)}
              </div>
            </div>

            {/* Section 2 */}
            <div>
              <div className='space-y-4'>
                <div className='bg-gray-50 p-4 rounded-lg'>
                  <span className='ml-2'>{data.section2.q2 || 'N/A'}</span>
                </div>
                <div className='bg-gray-50 p-4 rounded-lg'>
                  <span className='text-gray-500'>Is Chartered:</span>
                  <span className='ml-2'>{data.section2.isChartered}</span>
                </div>
                {renderDocumentSection('Articles of Incorporation', data.section2.articlesOfIncorporation)}
                {renderDocumentSection('By Laws', data.section2.byLaws)}
                {renderDocumentSection('Charter', data.section2.charter)}
              </div>
            </div>

            {/* Section 3 */}
            <div>
              <div className='space-y-4'>
                <div className='bg-gray-50 p-4 rounded-lg'>
                  <span className='ml-2'>{data.section3.q3 || 'N/A'}</span>
                </div>
                <div className='bg-gray-50 p-4 rounded-lg'>
                  <span className='text-gray-500'>Is Sponsored:</span>
                  <span className='ml-2'>{data.section3.isSponsored}</span>
                </div>
                {renderDocumentSection('Articles of Incorporation', data.section3.articlesOfIncorporation)}
                {renderDocumentSection('By Laws', data.section3.byLaws)}
                {renderDocumentSection('Charter', data.section3.charter)}
              </div>
            </div>

            {/* Board Members */}
            <div>
              <h3 className='text-lg font-medium text-gray-900 mb-4'>Board Members</h3>
              <div className='space-y-4'>
                {data.boardMembers.length > 0 ? (
                  data.boardMembers.map((member, index) => (
                    <div key={index} className='bg-gray-50 p-4 rounded-lg'>
                      <pre className='text-sm whitespace-pre-wrap'>{JSON.stringify(member, null, 2)}</pre>
                    </div>
                  ))
                ) : (
                  <div className='bg-gray-50 p-4 rounded-lg'>
                    <span className='text-gray-500'>No board members listed</span>
                  </div>
                )}
              </div>
            </div>

            {/* Evidence */}
            <div>
              <h3 className='text-lg font-medium text-gray-900 mb-4'>Evidence</h3>
              <div className='bg-gray-50 p-4 rounded-lg'>
                <span className='text-gray-500'>Provided Evidence:</span>
                <span className='ml-2'>{data.providedEvidence}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
